<template>
  <b-button
    ref="header"
    :style="style"
    class="bubbles-header"
    :class="{ 'selected-border': selected }"
    @click="updateStoryPath"
  >
    {{ details.name }}
  </b-button>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import _cloneDeep from 'lodash/cloneDeep'
import interact from 'interactjs'
import {
  getVisPosition,
  transformCoordinatesBubblesToScreen,
  transformCoordinatesScreenToBubbles,
  HEADER_BUTTON_ELEMENT_STYLE,
} from '@/components/public/Visualisations/Bubbles'
import { NODE_CONFIG_KEY, defaultVisPositions } from '@/constants'
import { Coordinate2D, NodeConfigKeys, StoryKeys, TreeNode } from '@/types'

const props = {
  details: {
    type: TreeNode,
    required: true,
  },
}

// eslint-disable-next-line no-use-before-define
@Component<BubblesHeader>({
  watch: {
    position() {
      this.top = this.position.y
      this.left = this.position.x
    },
    draggable(draggable: boolean) {
      if (draggable) {
        this.enableDragging()
      } else {
        this.disableDragging()
      }
    },
  },
})
export default class BubblesHeader extends Vue.extend({ props }) {
  width: number = 0
  height: number = 0
  top: number = 0
  left: number = 0

  mounted() {
    const header = this.$refs.header as HTMLElement
    this.width = header.clientWidth
    this.height = header.clientHeight
    this.top = this.position.y
    this.left = this.position.x

    if (this.draggable) {
      this.enableDragging()
    }
  }

  get draggable() {
    return this.$typedStore.public.display.showHandles
  }

  get bubblesVisualisationScaleFactor() {
    return this.$typedStore.public.size.bubblesVisualisationScaleFactor
  }

  get bubblesMinSizeSquareOffset() {
    return this.$typedStore.public.size.bubblesMinSizeSquareOffset
  }

  get levels() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.levels
  }

  get position(): Coordinate2D {
    const { headerPositions, viewerLevel, clusterHeadersHash } = this.$typedStore.visualisationData
    const position = getVisPosition(
      this.details.path.toString(),
      headerPositions,
      viewerLevel,
      clusterHeadersHash,
    )

    return transformCoordinatesBubblesToScreen(
      position,
      this.bubblesVisualisationScaleFactor,
      this.bubblesMinSizeSquareOffset,
    )
  }

  set position(newValue: Coordinate2D) {
    const position = transformCoordinatesScreenToBubbles(newValue)
    const { viewerLevel, clusterHeadersHash } = this.$typedStore.visualisationData
    const headerPositions =
      _cloneDeep(this.$typedStore.primary.entities.nodes.headerPositions(this.details.path)) ??
      defaultVisPositions()

    if (headerPositions.clusterPathsOverrides) {
      headerPositions.clusterPathsOverrides[clusterHeadersHash] = position
    } else if (viewerLevel === this.levels.maxLevel - 1) {
      headerPositions.default = position
    } else {
      if (!headerPositions.levelOverrides) {
        headerPositions.levelOverrides = {}
      }
      headerPositions.levelOverrides[viewerLevel] = position
    }

    this.$typedStore.primary.entities.nodes.updateNodeProp({
      path: this.details.path.toString(),
      configPath: [NODE_CONFIG_KEY, NodeConfigKeys.HEADER_POSITIONS],
      value: headerPositions,
    })
  }

  get selected() {
    return (
      this.$typedStore.activeVisualisation.storyPath.equals(this.details.path) && !this.draggable
    )
  }

  get level1() {
    return this.details.path.level1
  }

  get tabFontColor() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.tabFontColor(
      this.level1,
      StoryKeys.SIDEBAR_BOT,
    )
  }

  get style() {
    // Update Header.ts constants if the styling modifies the height and width of the element
    return {
      ...HEADER_BUTTON_ELEMENT_STYLE,
      'background-color':
        this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.colour(this.level1),
      color: this.tabFontColor,
      top: this.top - this.height / 2 + 'px',
      left: this.left - this.width / 2 + 'px',
      transform: `scale(${this.bubblesVisualisationScaleFactor})`,
    }
  }

  enableDragging() {
    const header = this.$refs.header as HTMLElement
    const bubblesSquare = document.querySelector('#bubblesMinSizeSquare')
    if (header && bubblesSquare) {
      const modifiers = [
        interact.modifiers.restrict({
          restriction: bubblesSquare as HTMLElement,
          elementRect: {
            top: 0,
            left: 0,
            bottom: 1,
            right: 1,
          },
        }),
      ]

      interact(header).draggable({
        modifiers,
        onmove: this.onmove,
        onend: this.onend,
      })
    }
  }

  disableDragging() {
    const header = this.$refs.header as HTMLElement

    if (header) {
      interact(header).unset()
    }
  }

  onmove(event: any) {
    // Incrementally change the position of the header
    this.top += event.dy
    this.left += event.dx
  }

  onend() {
    this.position = {
      x: this.left,
      y: this.top,
    }
  }

  updateStoryPath() {
    if (!this.draggable) {
      this.$typedStore.activeVisualisation.updateStoryPath(this.details.path.toString())
      this.$analytics.event('click', {
        event_category: 'sub-tier-header',
        event_label: this.details.path,
      })
    }
  }
}
</script>

<style scoped>
.selected-border {
  border-width: 7px !important;
  border-color: black !important;
}

.bubbles-header {
  /* Update Header.ts constants if the styling modifies the height and width of the element */
  position: absolute;
}
</style>
